import { ArrowLeft, GitBranch, Lock, MessageSquareText, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router';
import { api } from '../lib/api';
import type { AdminTicket, AdminTicketDetail, AttachmentPolicy, SavedReply, SupportPolicy, SupportReport, TicketStatus, TicketType } from '../lib/types';
import { Fold } from '../ui/Collapsible';
import { Sheet } from '../ui/Sheet';
import { AttachPicker, NewTicketForm, statusLabel, TICKET_TYPES, TicketRow, TicketThread, typeLabel, type Upload } from '../ui/Tickets';
import { useParent } from './ParentContext';

/** Admin → Support (#414): the Help & Feedback tickets from parents and students, worked here and escalated to GitHub. */

export interface SupportQueue {
  tickets: AdminTicket[];
  admins: string[];
  githubReady: boolean;
  /** Phase 3 (#446). */
  attachments?: AttachmentPolicy;
  savedReplies?: SavedReply[];
}

/** A saved reply with {name}, {child} and {number} filled in for this request (#446). */
function fillSavedReply(body: string, t: { number: number; openedBy: { kind: string; name: string; childName?: string } }) {
  const first = t.openedBy.name.trim().split(/\s+/)[0] || 'there';
  const child = t.openedBy.childName || (t.openedBy.kind === 'student' ? t.openedBy.name : 'your child');
  return body.replace(/\{name\}/g, first).replace(/\{child\}/g, child).replace(/\{number\}/g, String(t.number));
}

const STATUSES: TicketStatus[] = ['open', 'in_progress', 'waiting', 'resolved', 'closed'];
type Show = 'answer' | 'active' | 'done' | 'all';
const SHOW: Record<Show, { label: string; test: (t: AdminTicket) => boolean }> = {
  answer: { label: 'Needs an answer', test: (t) => !!t.needsAnswer },
  active: { label: 'Open', test: (t) => t.status !== 'resolved' && t.status !== 'closed' },
  done: { label: 'Resolved & closed', test: (t) => t.status === 'resolved' || t.status === 'closed' },
  all: { label: 'All', test: () => true },
};

export function AdminSupport({ queue, reload }: { queue: SupportQueue | null; reload: () => void }) {
  const { toast } = useParent();
  const [params, setParams] = useSearchParams();
  const openId = params.get('ticket');
  const [show, setShow] = useState<Show>('answer');
  const [type, setType] = useState<TicketType | ''>('');
  const [assignee, setAssignee] = useState('');
  const [adding, setAdding] = useState(false);
  const open = (id: string | null) => setParams(id ? { ticket: id } : {}, { replace: false });
  // Fresh numbers (and GitHub's state, which may have just been connected) each time the tab opens.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => reload(), []);

  if (openId) return <AdminTicketView id={openId} queue={queue} onBack={() => (open(null), reload())} />;
  if (!queue) return <p className="muted">Loading…</p>;

  const filtered = queue.tickets.filter(
    (t) => SHOW[show].test(t) && (!type || t.type === type) && (!assignee || (assignee === '-' ? !t.assignee : t.assignee === assignee)),
  );

  return (
    <div className="stack">
      <div className="row-between">
        <p className="muted grow">
          Questions, problems and ideas from parents and students. Reply here (they’re emailed), keep private notes, and escalate
          anything that needs code changes to a GitHub issue.
        </p>
        <button className="btn primary" onClick={() => setAdding(true)}>
          <Plus size={16} /> New ticket
        </button>
      </div>
      <SupportTiles queue={queue} />
      {!queue.githubReady && (
        <div className="card notice">
          GitHub isn’t connected, so tickets can’t be escalated yet. Connect it in <NavLink to="/admin/settings">Server settings</NavLink>.
        </div>
      )}
      <div className="chips" role="group" aria-label="Show">
        {(Object.keys(SHOW) as Show[]).map((s) => (
          <button key={s} className={`chip ${show === s ? 'active' : ''}`} onClick={() => setShow(s)}>
            {SHOW[s].label} ({queue.tickets.filter(SHOW[s].test).length})
          </button>
        ))}
      </div>
      <div className="row wrap support-filters">
        <label>
          Type
          <select value={type} onChange={(e) => setType(e.target.value as TicketType | '')}>
            <option value="">Any type</option>
            {TICKET_TYPES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Assigned to
          <select value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">Anyone</option>
            <option value="-">Nobody yet</option>
            {queue.admins.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>
      </div>
      <section className="card stack">
        {!filtered.length ? (
          <p className="muted">{show === 'answer' ? 'Nothing needs an answer right now.' : 'No tickets here.'}</p>
        ) : (
          filtered.map((t) => (
            <TicketRow
              key={t.id}
              t={t}
              forAdmin
              onOpen={() => open(t.id)}
              extra={
                <span className="ticket-row-tags">
                  {t.priority === 'high' && <span className="tag bug">High</span>}
                  {t.issueNumber && <span className="tag">Issue #{t.issueNumber}</span>}
                  {t.assignee && <span className="muted small">{t.assignee}</span>}
                </span>
              }
            />
          ))
        )}
      </section>
      {adding && (
        <Sheet title="New ticket" onClose={() => setAdding(false)}>
          <p className="muted small">Log an idea of your own, or something a parent told you about.</p>
          <NewTicketForm
            submitLabel="Open ticket"
            attachments={queue.attachments}
            onSubmit={async (t) => {
              try {
                const made = await api.post<AdminTicketDetail>('/api/admin/support/tickets', t);
                setAdding(false);
                toast({ text: `Ticket #${made.number} opened.`, tone: 'good' });
                open(made.id);
              } catch (e) {
                toast({ text: (e as Error).message, tone: 'alert' });
              }
            }}
          />
        </Sheet>
      )}
    </div>
  );
}

function AdminTicketView({ id, queue, onBack }: { id: string; queue: SupportQueue | null; onBack: () => void }) {
  const { toast } = useParent();
  const [t, setT] = useState<AdminTicketDetail | null | undefined>(undefined);
  const [text, setText] = useState('');
  const [internal, setInternal] = useState(false);
  const [after, setAfter] = useState<TicketStatus>('waiting');
  const [busy, setBusy] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [files, setFiles] = useState<(Upload & { size: number })[]>([]);

  useEffect(() => {
    api.get<AdminTicketDetail>(`/api/admin/support/tickets/${id}`).then(setT).catch(() => setT(null));
  }, [id]);

  async function act(fn: () => Promise<AdminTicketDetail>, done?: string) {
    setBusy(true);
    try {
      setT(await fn());
      if (done) toast({ text: done, tone: 'good' });
      return true;
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
      return false;
    } finally {
      setBusy(false);
    }
  }
  const patch = (body: object) => act(() => api.patch<AdminTicketDetail>(`/api/admin/support/tickets/${id}`, body));

  if (t === undefined) return <p className="muted">Loading…</p>;
  if (t === null)
    return (
      <div className="stack">
        <BackButton onBack={onBack} />
        <p className="muted">That ticket isn’t here anymore.</p>
      </div>
    );

  const who = t.openedBy;
  return (
    <div className="stack">
      <BackButton onBack={onBack} />
      <section className="card stack">
        <div className="row-between">
          <span className="muted small">
            #{t.number} · {typeLabel(t.type)} · opened {new Date(t.createdAt).toLocaleString()}
          </span>
          {t.issueNumber && (
            <a className="tag" href={t.issueUrl} target="_blank" rel="noreferrer">
              <GitBranch size={12} /> Issue #{t.issueNumber}
            </a>
          )}
        </div>
        <h2>{t.subject}</h2>
        <p className="small">
          {who.kind === 'admin' ? (
            <>Opened by {who.name} (admin)</>
          ) : who.kind === 'student' ? (
            <>
              From <strong>{who.name}</strong>, a student{t.familyName && <> in {t.familyName}</>}
            </>
          ) : (
            <>
              From <strong>{who.name}</strong> · <a href={`mailto:${who.email}`}>{who.email}</a>
              {t.familyName && <> · {t.familyName}</>}
            </>
          )}
        </p>
        <div className="row wrap support-controls">
          <label>
            Status
            <select value={t.status} disabled={busy} onChange={(e) => void patch({ status: e.target.value })}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {statusLabel(s, true)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Priority
            <select value={t.priority} disabled={busy} onChange={(e) => void patch({ priority: e.target.value })}>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </select>
          </label>
          <label>
            Assigned to
            <select value={t.assignee} disabled={busy} onChange={(e) => void patch({ assignee: e.target.value })}>
              <option value="">Nobody</option>
              {(queue?.admins ?? []).map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </label>
        </div>
        <TicketThread messages={t.messages} issue={{ number: t.issueNumber, url: t.issueUrl }} files="/api/admin/support/attachments" />

        <div className={`stack ticket-reply ${internal ? 'internal' : ''}`}>
          <div className="segmented" role="radiogroup" aria-label="Reply or note">
            <button role="radio" aria-checked={!internal} className={!internal ? 'active' : ''} onClick={() => setInternal(false)}>
              Reply
            </button>
            <button role="radio" aria-checked={internal} className={internal ? 'active' : ''} onClick={() => setInternal(true)}>
              <Lock size={13} /> Private note
            </button>
          </div>
          {!!queue?.savedReplies?.length && (
            <div className="saved-replies">
              <MessageSquareText size={15} className="muted" />
              <select
                value=""
                onChange={(e) => {
                  const r = queue.savedReplies!.find((x) => x.id === e.target.value);
                  if (r) setText((cur) => (cur.trim() ? `${cur.trimEnd()}\n\n` : '') + fillSavedReply(r.body, t));
                }}
                aria-label="Insert a saved reply"
              >
                <option value="">Insert a saved reply…</option>
                {queue.savedReplies.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.title}
                  </option>
                ))}
              </select>
            </div>
          )}
          <textarea
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={10000}
            aria-label={internal ? 'Private note' : 'Reply'}
            placeholder={internal ? 'Only admins see this.' : who.kind === 'admin' ? 'Add to the ticket…' : `Reply to ${who.name}…`}
          />
          <AttachPicker policy={queue?.attachments} files={files} onChange={setFiles} disabled={busy} />
          <div className="row wrap">
            {!internal && (
              <label className="inline">
                Then mark it
                <select value={after} onChange={(e) => setAfter(e.target.value as TicketStatus)}>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {statusLabel(s, true)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <button
              className="btn primary"
              disabled={busy || !text.trim()}
              onClick={async () => {
                const ok = await act(
                  () =>
                    api.post<AdminTicketDetail>(`/api/admin/support/tickets/${id}/messages`, {
                      body: text.trim(),
                      internal,
                      attachments: files.map(({ name, data }) => ({ name, data })),
                      ...(internal ? {} : { status: after }),
                    }),
                  internal ? 'Note added.' : 'Reply sent.',
                );
                if (ok) {
                  setText('');
                  setFiles([]);
                }
              }}
            >
              {internal ? 'Add note' : 'Send reply'}
            </button>
          </div>
        </div>

        {!t.issueNumber && (
          <div>
            <button className="btn" disabled={!queue?.githubReady} title={queue?.githubReady ? '' : 'Connect GitHub in Server settings first'} onClick={() => setEscalating(true)}>
              <GitBranch size={15} /> Escalate to GitHub
            </button>
          </div>
        )}
      </section>
      {escalating && (
        <EscalateSheet
          ticket={t}
          onClose={() => setEscalating(false)}
          onDone={(next) => {
            setEscalating(false);
            setT(next);
            toast({ text: `Escalated as GitHub issue #${next.issueNumber}.`, tone: 'good' });
          }}
        />
      )}
    </div>
  );
}

function BackButton({ onBack }: { onBack: () => void }) {
  return (
    <div>
      <button className="btn small" onClick={onBack}>
        <ArrowLeft size={15} /> All tickets
      </button>
    </div>
  );
}

/** File the ticket as a GitHub issue. The admin edits the text first; nothing about the family is sent (#414). */
function EscalateSheet({ ticket, onClose, onDone }: { ticket: AdminTicketDetail; onClose: () => void; onDone: (t: AdminTicketDetail) => void }) {
  const { toast } = useParent();
  const first = ticket.messages.find((m) => m.author.kind !== 'system' && !m.internal)?.body ?? '';
  const [title, setTitle] = useState(ticket.subject);
  const [body, setBody] = useState(first);
  const tellThem = ticket.openedBy.kind !== 'admin';
  const [reply, setReply] = useState(tellThem);
  const [replyText, setReplyText] = useState('Thanks for letting us know. We’ve passed this to our developers, and we’ll update you here.');
  const [busy, setBusy] = useState(false);

  async function escalate() {
    setBusy(true);
    try {
      onDone(await api.post<AdminTicketDetail>(`/api/admin/support/tickets/${ticket.id}/escalate`, { title, body, reply: reply ? replyText : '' }));
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title="Escalate to GitHub" onClose={onClose}>
      <div className="stack">
        <label>
          Issue title
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
        </label>
        <label>
          Issue description <span className="muted small">(edit out anything personal; it goes to GitHub as written)</span>
          <textarea rows={7} value={body} onChange={(e) => setBody(e.target.value)} />
        </label>
        {tellThem && (
          <>
            <label className="check">
              <input type="checkbox" checked={reply} onChange={(e) => setReply(e.target.checked)} />
              <span>Also reply to {ticket.openedBy.name}</span>
            </label>
            {reply && <textarea rows={3} value={replyText} onChange={(e) => setReplyText(e.target.value)} aria-label="Reply to the family" />}
          </>
        )}
        <div>
          <button className="btn primary" onClick={() => void escalate()} disabled={busy || title.trim().length < 3 || !body.trim() || (reply && !replyText.trim())}>
            <GitBranch size={15} /> {busy ? 'Filing…' : 'Create GitHub issue'}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

/** Report tiles (#414 phase 2): what's open, what needs an answer, how fast support answers and what got resolved. */
function SupportTiles({ queue }: { queue: SupportQueue }) {
  const [r, setR] = useState<SupportReport | null>(null);
  useEffect(() => {
    api.get<SupportReport>('/api/admin/support/report').then(setR).catch(() => setR(null));
  }, [queue]);
  if (!r) return null;
  const needs = queue.tickets.filter((t) => t.needsAnswer).length;
  const wait = r.avgFirstReplyMs;
  const waitText = wait === null ? '—' : wait < 3_600_000 ? `${Math.max(1, Math.round(wait / 60_000))} min` : wait < 172_800_000 ? `${(wait / 3_600_000).toFixed(1)} h` : `${Math.round(wait / 86_400_000)} days`;
  return (
    <div className="usage-tiles support-tiles" role="list" aria-label="Support at a glance">
      <SupportTile label="Open" value={String(r.open)} />
      <SupportTile label="Need an answer" value={String(needs)} tone={needs ? 'warn' : undefined} />
      <SupportTile label="Average first reply (30 days)" value={waitText} />
      <SupportTile label="Opened (30 days)" value={String(r.openedLast30Days)} />
      <SupportTile label="Resolved (30 days)" value={String(r.resolvedLast30Days)} />
      <SupportTile label="Open ideas" value={String(r.openByType.idea ?? 0)} />
    </div>
  );
}

function SupportTile({ label, value, tone }: { label: string; value: string; tone?: 'warn' }) {
  return (
    <div className={`usage-tile ${tone ?? ''}`} role="listitem">
      <span className="usage-value">{value}</span>
      <span className="muted small">{label}</span>
    </div>
  );
}

/** Admin → Server settings → Support (#414 phase 2): every Help & Feedback option, per the "configurable in Admin" rule. */
export function SupportSettings({
  value,
  onChange,
  notifyNew,
  onNotifyNew,
}: {
  value: SupportPolicy;
  onChange: (v: SupportPolicy) => void;
  notifyNew: boolean;
  onNotifyNew: (v: boolean) => void;
}) {
  const set = <K extends keyof SupportPolicy>(k: K, v: SupportPolicy[K]) => onChange({ ...value, [k]: v });
  const { toast } = useParent();
  async function checkGithubNow() {
    try {
      const r = await api.post<{ changed: number }>('/api/admin/support/github-sync');
      toast({ text: r.changed ? `${r.changed} request${r.changed === 1 ? '' : 's'} updated from GitHub.` : 'Nothing new on GitHub.', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  const toggleType = (t: TicketType, on: boolean) => {
    const next = TICKET_TYPES.map((x) => x.id).filter((x) => (x === t ? on : value.types.includes(x)));
    if (next.length) set('types', next);
  };
  // Fold summaries (#601).
  const contact = [value.contactEmail?.trim(), value.contactPhone?.trim()].filter(Boolean).join(' · ');
  const emailsOn = [notifyNew, value.emailAdminsOnReply, value.emailFamilyOnReply].filter(Boolean).length;
  const attachOn = value.attachmentsEnabled !== false;
  const replies = value.savedReplies?.length ?? 0;
  return (
    <div className="stack">
      <Fold title="Contact us" summary={contact || 'Hidden'}>
        <span className="muted small">Shown at the top of Help &amp; Feedback and in the account menu, for help that can’t wait. Leave the email and phone empty to hide it.</span>
        <label>
          Message
          <input value={value.contactNote ?? ''} onChange={(e) => set('contactNote', e.target.value)} maxLength={200} />
        </label>
        <label>
          Support email
          <input type="email" value={value.contactEmail ?? ''} onChange={(e) => set('contactEmail', e.target.value)} maxLength={120} />
        </label>
        <label>
          Support phone (call and text)
          <input type="tel" value={value.contactPhone ?? ''} onChange={(e) => set('contactPhone', e.target.value)} maxLength={30} />
        </label>
      </Fold>
      <Fold
        title="What parents and students can ask about"
        summary={value.types.length === TICKET_TYPES.length ? `All ${TICKET_TYPES.length} kinds` : `${value.types.length} of ${TICKET_TYPES.length} kinds`}
      >
        <div className="stack" role="group" aria-label="What parents and students can ask about">
        {TICKET_TYPES.map((t) => (
          <label key={t.id} className="check">
            <input type="checkbox" checked={value.types.includes(t.id)} onChange={(e) => toggleType(t.id, e.target.checked)} disabled={value.types.length === 1 && value.types.includes(t.id)} />
            <span>
              {t.label}
              {t.id === 'account' && <span className="muted small block">Parents only; students ask a parent.</span>}
            </span>
          </label>
        ))}
        </div>
      </Fold>
      <Fold title="Limits" summary={`${value.autoCloseDays ? `Close after ${value.autoCloseDays} day${value.autoCloseDays === 1 ? '' : 's'}` : 'Never close'} · ${value.openPerHour} an hour`}>
      <div className="field-row">
        <label>
          Close resolved requests after (days)
          <input type="number" min={0} max={365} value={value.autoCloseDays} onChange={(e) => set('autoCloseDays', Math.max(0, Number(e.target.value) || 0))} />
        </label>
        <label>
          New requests per person per hour
          <input type="number" min={1} max={100} value={value.openPerHour} onChange={(e) => set('openPerHour', Math.max(1, Number(e.target.value) || 1))} />
        </label>
      </div>
      <p className="muted small">
        A resolved request closes after {value.autoCloseDays || 'no'} day{value.autoCloseDays === 1 ? '' : 's'} without a reply
        {value.autoCloseDays ? '' : ' (it stays resolved; 0 means never close)'}. A reply before then reopens it.
      </p>
      </Fold>
      <Fold title="Emails" summary={`${emailsOn} of 3 on`}>
        <label className="check">
          <input type="checkbox" checked={notifyNew} onChange={(e) => onNotifyNew(e.target.checked)} />
          <span>Email the admins when a new request arrives</span>
        </label>
        <label className="check">
          <input type="checkbox" checked={value.emailAdminsOnReply} onChange={(e) => set('emailAdminsOnReply', e.target.checked)} />
          <span>
            Email the admins when a family replies
            <span className="muted small block">Only the assigned admin, once a request has one.</span>
          </span>
        </label>
        <label className="check">
          <input type="checkbox" checked={value.emailFamilyOnReply} onChange={(e) => set('emailFamilyOnReply', e.target.checked)} />
          <span>
            Email the family when support replies
            <span className="muted small block">Each parent can still turn these off in Settings → Notifications. Private notes are never sent.</span>
          </span>
        </label>
      </Fold>
      <Fold
        title="Screenshots and files"
        summary={attachOn ? `On · up to ${value.attachmentMaxMb ?? 5} MB${value.allowPdf !== false ? ' · PDFs too' : ''}` : 'Off'}
      >
        <label className="check">
          <input type="checkbox" checked={value.attachmentsEnabled !== false} onChange={(e) => set('attachmentsEnabled', e.target.checked)} />
          <span>
            Families and admins can attach screenshots
            <span className="muted small block">Pictures (PNG, JPG, GIF, WebP). They’re kept with the request and backed up with it.</span>
          </span>
        </label>
        <label className="check">
          <input type="checkbox" checked={value.allowPdf !== false} disabled={value.attachmentsEnabled === false} onChange={(e) => set('allowPdf', e.target.checked)} />
          <span>PDFs too</span>
        </label>
        <div className="field-row">
          <label>
            Largest file (MB)
            <input type="number" min={1} max={10} value={value.attachmentMaxMb ?? 5} disabled={value.attachmentsEnabled === false} onChange={(e) => set('attachmentMaxMb', Math.min(10, Math.max(1, Number(e.target.value) || 1)))} />
          </label>
          <label>
            Files per message
            <input type="number" min={1} max={5} value={value.attachmentsPerMessage ?? 3} disabled={value.attachmentsEnabled === false} onChange={(e) => set('attachmentsPerMessage', Math.min(5, Math.max(1, Number(e.target.value) || 1)))} />
          </label>
        </div>
      </Fold>
      <Fold title="When a GitHub issue is closed" summary={value.githubSync !== false ? 'Resolve and tell the family' : 'Off'}>
        <label className="check">
          <input type="checkbox" checked={value.githubSync !== false} onChange={(e) => set('githubSync', e.target.checked)} />
          <span>
            Resolve the request and tell the family
            <span className="muted small block">
              Checked every 15 minutes. Closed as “not planned” only adds a private note, so you can decide what to say.
            </span>
          </span>
        </label>
        <label>
          What the family is told
          <textarea rows={3} maxLength={2000} value={value.fixedReply ?? ''} disabled={value.githubSync === false} onChange={(e) => set('fixedReply', e.target.value)} />
        </label>
        <div>
          <button type="button" className="btn small" onClick={() => void checkGithubNow()}>
            <RefreshCw size={14} /> Check GitHub now
          </button>
        </div>
      </Fold>
      <Fold title="Saved replies" summary={replies ? `${replies} saved` : 'None yet'}>
        <SavedRepliesEditor value={value.savedReplies ?? []} onChange={(v) => set('savedReplies', v)} />
      </Fold>
      <Fold
        title="GitHub label for each kind, when escalated"
        summary={value.escalationLabel?.trim() ? `Plus “${value.escalationLabel.trim()}” on every issue` : 'One label per kind'}
      >
        <div className="field-row wrap-row">
          {TICKET_TYPES.map((t) => (
            <label key={t.id}>
              {t.label}
              <input value={value.labels[t.id]} maxLength={50} onChange={(e) => set('labels', { ...value.labels, [t.id]: e.target.value })} />
            </label>
          ))}
        </div>
        <label>
          Also label every escalated issue
          <input value={value.escalationLabel ?? 'from-support'} maxLength={50} onChange={(e) => set('escalationLabel', e.target.value)} placeholder="leave empty for none" />
          <span className="muted small block">
            So on GitHub you can tell the issues that came from a family’s request from development issues. Each escalated issue also links back to its request
            here.
          </span>
        </label>
      </Fold>
    </div>
  );
}

/** Saved replies (#446): canned answers admins drop into a reply. {name}, {child} and {number} are filled in. */
function SavedRepliesEditor({ value, onChange }: { value: SavedReply[]; onChange: (v: SavedReply[]) => void }) {
  const update = (i: number, patch: Partial<SavedReply>) => onChange(value.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="stack">
      <p className="muted small">
        Answers you give often. Insert one from a request’s reply box, then edit it before sending. <code>{'{name}'}</code> becomes the person’s first
        name, <code>{'{child}'}</code> their child, and <code>{'{number}'}</code> the request number.
      </p>
      {value.map((r, i) => (
        <div key={r.id || i} className="stack saved-reply-edit">
          <div className="row-between">
            <input value={r.title} maxLength={80} placeholder="Title, e.g. Restart the app" onChange={(e) => update(i, { title: e.target.value })} aria-label="Saved reply title" className="grow" />
            <button type="button" className="icon-btn" aria-label={`Delete ${r.title || 'saved reply'}`} onClick={() => onChange(value.filter((_, j) => j !== i))}>
              <Trash2 size={16} />
            </button>
          </div>
          <textarea rows={3} maxLength={5000} value={r.body} onChange={(e) => update(i, { body: e.target.value })} aria-label="Saved reply text" />
        </div>
      ))}
      <div>
        <button type="button" className="btn small" disabled={value.length >= 50} onClick={() => onChange([...value, { id: '', title: '', body: '' }])}>
          <Plus size={14} /> Add a saved reply
        </button>
      </div>
    </div>
  );
}
